import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createLot,
  updateLot,
} from "../../lib/api-modules/accounts.js";
import type {
  AccountLotDetail,
  CreateLotInput,
  LotTaxTerm,
  UpdateLotInput,
} from "../../lib/api-modules/types.js";
import { centsToDollars, dollarsToCents } from "../../lib/util/money.js";
import { ErrorMessage } from "../auth/error-message.js";

/**
 * <LotForm> — manual lot create/edit (Phase 3 Session 2).
 *
 * - Create mode: ticker is pre-filled and disabled when caller passes
 *   it (e.g. opening from a holding's "Add lot" button); otherwise
 *   editable.
 * - Edit mode: ticker is editable; backend handles SK rewrite if it
 *   changes (Delete-old-SK + Put-new-SK in TransactWriteItems).
 *
 * Money inputs are dollars, converted at submit. Unit cost defaults
 * to `cost_basis / quantity` when blank — locks once the user types.
 *
 * `tax_term` is auto-suggested as `long` when `purchase_date` is more
 * than a year ago; user can override.
 */

const TAX_TERM_OPTIONS: ReadonlyArray<{ value: LotTaxTerm; label: string }> = [
  { value: "short", label: "Short-term (≤ 1 year)" },
  { value: "long", label: "Long-term (> 1 year)" },
];

interface LotFormProps {
  readonly mode: "create" | "edit";
  readonly accountId: string;
  /** Pre-fill ticker on create (locks the field). Ignored in edit mode. */
  readonly tickerHint?: string;
  readonly lot?: AccountLotDetail & { ticker: string };
  readonly onClose: () => void;
}

export function LotForm({
  mode,
  accountId,
  tickerHint,
  lot,
  onClose,
}: LotFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const tickerLocked = !isEdit && tickerHint !== undefined;

  const [ticker, setTicker] = useState<string>(
    lot?.ticker ?? tickerHint ?? "",
  );
  const [purchaseDate, setPurchaseDate] = useState<string>(
    lot?.purchase_date ?? todayIsoDate(),
  );
  const [quantity, setQuantity] = useState<string>(
    lot?.quantity?.toString() ?? "",
  );
  const [costBasis, setCostBasis] = useState<string>(
    lot?.cost_basis_cents !== undefined && lot?.cost_basis_cents !== null
      ? centsToDollars(lot.cost_basis_cents)
      : "",
  );
  const [unitCost, setUnitCost] = useState<string>(
    lot?.unit_cost_cents !== undefined && lot?.unit_cost_cents !== null
      ? centsToDollars(lot.unit_cost_cents)
      : "",
  );
  const [taxTerm, setTaxTerm] = useState<LotTaxTerm | "">(
    lot?.tax_term ?? "",
  );
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Auto-suggest tax_term when purchase_date is > 1y old.
  const suggestedTerm = useMemo<LotTaxTerm | null>(() => {
    if (!purchaseDate) return null;
    const days = daysSince(purchaseDate);
    if (days === null) return null;
    return days > 365 ? "long" : "short";
  }, [purchaseDate]);

  const mutation = useMutation({
    mutationFn: async () => {
      const tickerNorm = ticker.trim().toUpperCase();
      const qty = Number(quantity);
      const costCents = dollarsToCents(costBasis);
      const unitCents =
        unitCost.trim().length > 0
          ? dollarsToCents(unitCost)
          : qty > 0
            ? Math.round(costCents / qty)
            : 0;
      const term = (taxTerm || suggestedTerm) ?? undefined;

      if (isEdit && lot) {
        const input: UpdateLotInput = {
          ticker: tickerNorm,
          purchase_date: purchaseDate,
          quantity: qty,
          cost_basis_cents: costCents,
          unit_cost_cents: unitCents,
          ...(term ? { tax_term: term } : {}),
        };
        return updateLot(accountId, lot.lot_id, input);
      }
      const input: CreateLotInput = {
        ticker: tickerNorm,
        purchase_date: purchaseDate,
        quantity: qty,
        cost_basis_cents: costCents,
        unit_cost_cents: unitCents,
        ...(term ? { tax_term: term } : {}),
      };
      return createLot(accountId, input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      // Holdings refetch is delayed because the recompute now runs via
      // the lots-table DDB stream (Phase 3.4); 800ms covers the median
      // streams replay tail. Cold-start P99 can hit 5s; the subsequent
      // refetch resolves it.
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ["accounts", accountId, "holdings"],
        });
      }, 800);
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save lot. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    if (
      ticker.trim().length === 0 ||
      purchaseDate.length === 0 ||
      quantity.trim().length === 0 ||
      costBasis.trim().length === 0
    ) {
      setInlineError("Ticker, date, quantity, and cost basis are required.");
      return;
    }
    if (Number(quantity) <= 0) {
      setInlineError("Quantity must be greater than zero.");
      return;
    }
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit lot" : "Add lot"}
      className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {isEdit ? "Edit lot" : "Add lot"}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">Ticker</label>
          <input
            type="text"
            className={inputClass}
            value={ticker}
            onChange={(e) => setTicker(e.target.value)}
            disabled={mutation.isPending || tickerLocked}
            placeholder="AAPL"
            autoFocus={!tickerLocked}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Purchase date
          </label>
          <input
            type="date"
            className={inputClass}
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Quantity</label>
          <input
            type="number"
            className={inputClass}
            value={quantity}
            step="0.0001"
            min="0"
            onChange={(e) => setQuantity(e.target.value)}
            disabled={mutation.isPending}
            autoFocus={tickerLocked}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Cost basis ($)
          </label>
          <input
            type="text"
            inputMode="decimal"
            className={inputClass}
            value={costBasis}
            placeholder="0.00"
            onChange={(e) => setCostBasis(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Unit cost ($){" "}
            <span className="text-zinc-500">
              (defaults to cost ÷ quantity)
            </span>
          </label>
          <input
            type="text"
            inputMode="decimal"
            className={inputClass}
            value={unitCost}
            placeholder="auto"
            onChange={(e) => setUnitCost(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Tax term</label>
          <select
            className={inputClass}
            value={taxTerm}
            onChange={(e) => setTaxTerm(e.target.value as LotTaxTerm | "")}
            disabled={mutation.isPending}
          >
            <option value="">
              Auto ({suggestedTerm ?? "—"})
            </option>
            {TAX_TERM_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : isEdit ? "Save lot" : "Add lot"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>

      {inlineError && (
        <ErrorMessage>{inlineError}</ErrorMessage>
      )}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";

function todayIsoDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function daysSince(iso: string): number | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}
